import { Fetch } from '.';
import { Metadata } from '../types/Metadata';
import { View } from '../types/View';
import { ManagedDataPayload } from '../types/ManagedData';

export interface BacktagData {
  itemId: number;
  topicCategoryIds: number[];
  taxonomyTagIds: number[];
  rolePriorityIds: number[];
  priorityJourneyStepIds: number[];
}

export interface BacktagImport {
  fileName: string;
  totalItems: number;
  totalTCs: number;
  totalTTs: number;
  totalRPs: number;
  totalJSs: number;
}

export interface Payload {
  data: Partial<Metadata>;
  refIds: string[];
  maxResults: number;
  filters: View.FiltersField[];
  backtagImport: BacktagImport;
  backtagData: BacktagData[];
  itemIds: number[];
  tcIds: number[];
  ttIds: number[];
  rpIds: number[];
  jsIds: number[];
}

export const importBacktagData =
  (f: Fetch) =>
  async (
    toasterId: string,
    payload: Pick<Payload, 'backtagData' | 'backtagImport'>
  ): Promise<Metadata[]> =>
    f.fetchJSON<Metadata[]>(payload, '/backtag/import/' + toasterId);

export const getBacktagImport =
  (f: Fetch) =>
  async (toasterId: string): Promise<Metadata> =>
    f.getJSON<Metadata>('/backtag/import/' + toasterId);

export const validateBacktagImportData =
  (f: Fetch) =>
  async (
    payload: Pick<Payload, 'itemIds' | 'tcIds' | 'ttIds' | 'rpIds' | 'jsIds'>
  ) =>
    f.fetchJSON<{
      missingItemIds: number[];
      missingTCIds: number[];
      missingTTIds: number[];
      missingRPIds: number[];
      missingJSIds: number[];
    }>(payload, '/backtag/import/validate');

export const addBackTags =
  (f: Fetch) =>
  async (payload: Pick<Payload, 'refIds' | 'data'>): Promise<Metadata[]> =>
    f.fetchJSON<Metadata[]>(payload, '/backtag/add-tags');

export const searchStagedTags =
  (f: Fetch) =>
  async (
    payload: Pick<Payload, 'maxResults' | 'filters'>
  ): Promise<Metadata[]> =>
    f.fetchJSON<Metadata[]>(payload, '/backtag/staged-tags/search', 'POST');

export const publishStagedTags =
  (f: Fetch) =>
  async ({ data }: Pick<Payload, 'data'>): Promise<Metadata[]> => {
    const taxonomyTagRefIds = (data?.props?.taxonomyTags || [])
      .map(tag => tag?.props?.refId)
      .filter(x => x);
    const rolePriorityRefIds = (data?.props?.rolePriorities || [])
      .map(rp => rp?.props?.refId)
      .filter(x => x);
    return await f.fetchJSON<Metadata[]>(
      { taxonomyTagRefIds, rolePriorityRefIds },
      '/backtag/staged-tags/publish',
      'POST'
    );
  };

export const getStagedTagsItemCount =
  (f: Fetch) => async (payload: Pick<Payload, 'data'>) =>
    f.fetchJSON<{ count: number; curTimeInMs: number }>(
      payload,
      '/backtag/staged-tags/count',
      'POST'
    );

export const importManagedData =
  (f: Fetch) =>
  async (
    payload: Pick<ManagedDataPayload, 'managedData' | 'managedDataImport'>
  ): Promise<Metadata[]> => {
    return f.fetchJSON<Metadata[]>(
      payload,
      '/backtag/import/kmap-managed-data'
    );
  };
